import React from 'react';
import { PlayIcon, ClockIcon, HeartIcon, MoreHorizontalIcon, ArrowLeftIcon } from 'lucide-react';
interface Track {
  id: string;
  title: string;
  artist: string;
  album?: string;
  duration: string;
  popularity?: number;
}
interface PlaylistViewProps {
  playlist: any;
  isAlbum?: boolean;
  onBack: () => void;
}
export const PlaylistView = ({
  playlist,
  isAlbum = false,
  onBack
}: PlaylistViewProps) => {
  // Ensure playlist exists before accessing properties
  if (!playlist) {
    return <div className="min-h-full bg-gradient-to-b from-[#333333] to-[#121212] p-8" data-id="element-462">
        <button onClick={onBack} className="p-2 rounded-full bg-black/40 hover:bg-black/60 transition-colors" data-id="element-463">
          <ArrowLeftIcon className="w-5 h-5" data-id="element-464" />
        </button>
        <div className="text-center mt-8" data-id="element-465">Loading...</div>
      </div>;
  }
  // Generate mock tracks if not provided, ensuring we always have an array
  const tracks: Track[] = Array.isArray(playlist.tracks) ? playlist.tracks : Array(12).fill(null).map((_, i) => ({
    id: `track-${i}`,
    title: isAlbum ? ['Blinding Lights', 'Save Your Tears', 'In Your Eyes', 'After Hours', 'Heartless', 'Too Late'][i % 6] : ['Bohemian Rhapsody', 'Stairway to Heaven', 'Imagine', 'Billie Jean', 'Smells Like Teen Spirit', 'Let It Be'][i % 6],
    artist: isAlbum ? playlist.artist : ['Queen', 'Led Zeppelin', 'John Lennon', 'Michael Jackson', 'Nirvana', 'The Beatles'][i % 6],
    album: isAlbum ? playlist.title : ['A Night at the Opera', 'Led Zeppelin IV', 'Imagine', 'Thriller', 'Nevermind', 'Let It Be'][i % 6],
    duration: `${Math.floor(Math.random() * 2) + 2}:${Math.floor(Math.random() * 59).toString().padStart(2, '0')}`,
    popularity: Math.floor(Math.random() * 100)
  }));
  const totalDuration = '1 hr 12 min';
  return <div className="min-h-full bg-gradient-to-b from-[#333333] to-[#121212]" data-id="element-466">
      {/* Header */}
      <div className="p-6 flex flex-col md:flex-row items-start md:items-end space-y-6 md:space-y-0 md:space-x-6" data-id="element-467">
        <button onClick={onBack} className="absolute top-4 left-4 p-2 rounded-full bg-black/40 hover:bg-black/60 transition-colors" data-id="element-468">
          <ArrowLeftIcon className="w-5 h-5" data-id="element-469" />
        </button>
        <div className="w-48 h-48 flex-shrink-0 shadow-2xl" data-id="element-470">
          <img src={playlist.imageUrl || 'https://images.unsplash.com/photo-1611339555312-e607c8352fd7?w=300&h=300&fit=crop'} alt={playlist.title} className="w-full h-full object-cover" onError={e => {
          e.currentTarget.src = 'https://images.unsplash.com/photo-1611339555312-e607c8352fd7?w=300&h=300&fit=crop';
        }} data-id="element-471" />
        </div>
        <div className="flex flex-col justify-end max-w-full" data-id="element-472">
          <p className="text-sm uppercase font-bold" data-id="element-473">
            {isAlbum ? 'Album' : 'Playlist'}
          </p>
          <h1 className="text-3xl md:text-5xl font-bold my-2 break-words" data-id="element-474">
            {playlist.title}
          </h1>
          <div className="flex flex-wrap items-center text-sm text-gray-300 space-x-1" data-id="element-475">
            {isAlbum ? <>
                <span className="font-bold text-white" data-id="element-476">{playlist.artist}</span>
                <span data-id="element-477">•</span>
                <span data-id="element-478">{playlist.releaseYear}</span>
                <span data-id="element-479">•</span>
                <span data-id="element-480">{tracks.length} songs</span>
                <span data-id="element-481">•</span>
                <span data-id="element-482">{totalDuration}</span>
              </> : <>
                <span className="font-bold text-white" data-id="element-483">Spotify</span>
                <span data-id="element-484">•</span>
                <span data-id="element-485">{tracks.length} songs</span>
                <span data-id="element-486">•</span>
                <span data-id="element-487">{totalDuration}</span>
              </>}
          </div>
        </div>
      </div>
      {/* Controls */}
      <div className="px-6 py-4 flex items-center space-x-4" data-id="element-488">
        <button className="bg-green-500 text-black rounded-full p-3 hover:scale-105 transition-transform" data-id="element-489">
          <PlayIcon className="w-6 h-6 md:w-8 md:h-8" data-id="element-490" />
        </button>
        <button className="text-white hover:text-green-500 transition-colors" data-id="element-491">
          <HeartIcon className="w-6 h-6 md:w-8 md:h-8" data-id="element-492" />
        </button>
        <button className="text-white hover:text-green-500 transition-colors" data-id="element-493">
          <MoreHorizontalIcon className="w-6 h-6 md:w-8 md:h-8" data-id="element-494" />
        </button>
      </div>
      {/* Tracks list */}
      <div className="px-4 md:px-6 pb-28 overflow-x-hidden" data-id="element-495">
        <table className="w-full text-left text-gray-400" data-id="element-496">
          <thead data-id="element-497">
            <tr className="border-b border-white/10" data-id="element-498">
              <th className="py-2 w-8" data-id="element-499">#</th>
              <th className="py-2" data-id="element-500">TITLE</th>
              {!isAlbum && <th className="py-2 hidden md:table-cell" data-id="element-501">ALBUM</th>}
              <th className="py-2 hidden md:table-cell" data-id="element-502">DATE ADDED</th>
              <th className="py-2 text-right" data-id="element-503">
                <ClockIcon className="w-5 h-5 inline" data-id="element-504" />
              </th>
            </tr>
          </thead>
          <tbody data-id="element-505">
            {tracks.map((track, index) => <tr key={track.id} className="hover:bg-white/10 group" data-id="element-506">
                <td className="py-3 px-1" data-id="element-507">
                  <div className="flex items-center justify-center w-6" data-id="element-508">
                    <span className="group-hover:hidden" data-id="element-509">{index + 1}</span>
                    <PlayIcon className="w-4 h-4 hidden group-hover:block" data-id="element-510" />
                  </div>
                </td>
                <td className="py-3" data-id="element-511">
                  <div className="flex items-center" data-id="element-512">
                    <div className="overflow-hidden" data-id="element-513">
                      <div className="text-white truncate max-w-[200px] md:max-w-none" data-id="element-514">
                        {track.title}
                      </div>
                      <div className="text-sm truncate max-w-[200px] md:max-w-none" data-id="element-515">
                        {track.artist}
                      </div>
                    </div>
                  </div>
                </td>
                {!isAlbum && <td className="py-3 hidden md:table-cell" data-id="element-516">{track.album}</td>}
                <td className="py-3 hidden md:table-cell" data-id="element-517">3 days ago</td>
                <td className="py-3 text-right px-1" data-id="element-518">{track.duration}</td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
};